/**
 * Nombre y sucursal del asesor de venta presencial, para precargar la pantalla
 * del vendedor (US 23446). Se guardan en localStorage por una jornada (8 horas
 * desde el último uso: cada "Empezar a cotizar" renueva el plazo): en celular y
 * tablet el navegador cierra pestañas solo, y con sessionStorage el asesor
 * tendría que cargarlos varias veces por día. El registro de cada lead NO lee
 * de acá: usa la copia que se guarda en la cotización al empezar.
 */
export const CLAVE_DATOS_VENDEDOR = 'venta-presencial-vendedor';
export const MAX_NOMBRE_VENDEDOR = 50;
/** Una jornada laboral. */
export const DURACION_DATOS_MS = 8 * 60 * 60 * 1000;

export interface DatosVendedor {
  nombre: string;
  /** Código de la sucursal que entrega el BFF; vacío si el origen no tenía lista cargada. */
  sucursal: string;
}

interface Guardado {
  nombre: string;
  sucursal: string;
  vence: number;
}

/** Recorta por caracteres (no por unidades UTF-16), así no parte un emoji a la mitad. */
export const normalizarNombre = (nombre: string): string =>
  Array.from(nombre.trim()).slice(0, MAX_NOMBRE_VENDEDOR).join('');

export const borrarDatosVendedor = (): void => {
  try {
    window.localStorage.removeItem(CLAVE_DATOS_VENDEDOR);
  } catch {
    // Storage bloqueado (modo privado estricto): no hay nada que borrar.
  }
};

export const guardarDatosVendedor = (datos: DatosVendedor, ahora: Date = new Date()): void => {
  const guardado: Guardado = {
    nombre: normalizarNombre(datos.nombre),
    sucursal: datos.sucursal,
    vence: ahora.getTime() + DURACION_DATOS_MS,
  };
  try {
    window.localStorage.setItem(CLAVE_DATOS_VENDEDOR, JSON.stringify(guardado));
  } catch {
    // Storage lleno o bloqueado: se sigue cotizando, solo que sin recordar los datos.
  }
};

/** Los datos vigentes, o null si no hay o vencieron. Si la sucursal sigue existiendo lo decide quien los usa. */
export const leerDatosVendedor = (ahora: Date = new Date()): DatosVendedor | null => {
  let crudo: string | null;
  try {
    crudo = window.localStorage.getItem(CLAVE_DATOS_VENDEDOR);
  } catch {
    return null;
  }
  if (!crudo) return null;
  try {
    const guardado = JSON.parse(crudo) as Partial<Guardado>;
    // La sucursal puede estar vacía (origen sin lista), pero tiene que venir: sin ella es un formato viejo.
    const { sucursal } = guardado;
    const nombre = typeof guardado.nombre === 'string' ? normalizarNombre(guardado.nombre) : '';
    const vigente = typeof guardado.vence === 'number' && ahora.getTime() <= guardado.vence;
    if (typeof sucursal !== 'string' || nombre === '' || !vigente) {
      borrarDatosVendedor();
      return null;
    }
    return { nombre, sucursal: sucursal.trim() };
  } catch {
    borrarDatosVendedor();
    return null;
  }
};
